import { FC, useEffect } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Async
import { getInvoicesSummaryBreakdownByAccount } from "app/store/BillingInvoices/BillingInvoices.async";
// Actions
import { BillingInvoicesActions } from "app/store/BillingInvoices/BillingInvoices.slice";
// Selectors
import { selectSortedInvoicesSummaryBreakdownByAccount, selectInvoicesSummaryBreakdownByAccountSort } from "app/store/BillingInvoices/BillingInvoices.selectors";
// Components
import { Loader } from "app/components/Utilities";
import SummaryRow from "./DashboardTableRow";
// MUI
import {
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Box, Typography
} from "@mui/material";
// Icons
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';

const DashboardTable:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const invoicesSummary = useAppSelector(selectSortedInvoicesSummaryBreakdownByAccount);
  const sort = useAppSelector(selectInvoicesSummaryBreakdownByAccountSort);

  useEffect(() => {
    dispatch(getInvoicesSummaryBreakdownByAccount({}));
    return () => {
      dispatch(BillingInvoicesActions.setInitialField('invoicesSummaryBreakdownByAccount'));
      dispatch(BillingInvoicesActions.setInitialField('invoicesSummaryBreakdownByAccountSort'));
    }
    // eslint-disable-next-line
  }, []);

  const onClick = (sortBy:string) => {
    const direction = sort.sortBy === sortBy && sort.direction === 'asc' ? 'desc' : 'asc';
    dispatch(BillingInvoicesActions.setInvoicesSummaryBreakdownByAccountSort({ sortBy, direction }));
  }

  const TableCellItem: FC<{ value: string }> = ({ value }) => {
    const isActive = sort.sortBy === value;

    return (
      <TableCell
        onClick={() => onClick(value)}
        sx={{
          cursor: 'pointer',
          '&:hover .sortArrow': {
            opacity: isActive ? 1 : 0.3,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>{t(`labels.${value}`)}</Typography>

          <Box
            className="sortArrow"
            sx={{
              display: 'flex',
              opacity: isActive ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            {isActive && sort.direction === 'asc' ? (
              <KeyboardArrowUpIcon color="action" />
            ) : (
              <KeyboardArrowDownIcon color="action" />
            )}
          </Box>
        </Box>
      </TableCell>
    );
  };

  if ( !invoicesSummary ) return <Loader />;
  return (
    <TableContainer component={Paper} sx={{ height: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCellItem value="company" />
            <TableCellItem value="outstanding" />
            <TableCellItem value="overdue" />
            <TableCellItem value="upcoming" />
          </TableRow>
        </TableHead>
        <TableBody>
          {invoicesSummary.map((summary) => (
            <SummaryRow key={summary.account.id} summary={summary} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default DashboardTable;
